import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as CashIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { Payment, Currency, PaymentMethod } from '../../types/PaymentLink';

interface PaymentHistorySubRowProps {
  payments: Payment[];
  currency: Currency;
  totalPaid: number;
  invoiceAmount: number;
  remainingAmount: number;
  onEditManualPayment?: (payment: Payment) => void;
  onDeleteManualPayment?: (payment: Payment) => void;
  manualActionLoadingPaymentId?: string | null;
}

const PaymentHistorySubRow: React.FC<PaymentHistorySubRowProps> = ({
  payments,
  currency,
  totalPaid,
  invoiceAmount,
  remainingAmount,
  onEditManualPayment,
  onDeleteManualPayment,
  manualActionLoadingPaymentId,
}) => {
  const activePayments = payments.filter((payment) => payment.status === 'success' && !payment.isDeleted);

  const getPaymentTypeLabel = (payment: Payment): string => {
    if (payment.paymentType === 'deposit') return 'Deposit';
    if (payment.paymentType === 'full') return 'Full Payment';
    return 'Partial Payment';
  };

  const getPaymentTypeColor = (payment: Payment): 'success' | 'info' | 'primary' => {
    if (payment.paymentType === 'deposit') return 'info';
    if (payment.paymentType === 'full') return 'success';
    return 'primary';
  };

  // Helper for manual payment method labels
  const getPaymentMethodLabel = (method: PaymentMethod): string => {
    const labels: Record<PaymentMethod, string> = {
      cash: 'Cash (Nakit)',
      credit_card: 'Credit Card (Kredi Kartı)',
      bank_transfer: 'Bank Transfer (Havale/EFT)',
      wire_transfer: 'Wire Transfer',
      belek: 'Belek',
      other: 'Other',
    };
    return labels[method] || method;
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (timestamp: { toDate: () => Date }): string => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        Payment History ({activePayments.length} {activePayments.length === 1 ? 'payment' : 'payments'})
      </Typography>

      {activePayments.length === 0 && (
        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white' }}>
          <Typography variant="body2" color="text.secondary">
            No active payments to display.
          </Typography>
        </Paper>
      )}

      <Stack spacing={2}>
        {activePayments.map((payment, index) => (
          <Paper
            key={payment.id || index}
            elevation={1}
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderLeft: '4px solid',
              borderLeftColor: payment.isManual ? 'warning.main' : 'success.main',
            }}
          >
            <Stack spacing={1.5}>
              {/* Amount and Type */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCurrency(payment.amount)}
                    </Typography>
                  </Box>
                  {payment.isManual && onEditManualPayment && onDeleteManualPayment && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="Edit Manual Payment">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEditManualPayment(payment)}
                            disabled={manualActionLoadingPaymentId === payment.id}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete Manual Payment">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeleteManualPayment(payment)}
                            disabled={manualActionLoadingPaymentId === payment.id}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={getPaymentTypeLabel(payment)}
                    color={getPaymentTypeColor(payment)}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {payment.isManual && (
                    <Chip
                      label="Manual"
                      color="warning"
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              </Box>

              {/* Date */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(payment.timestamp)}
                </Typography>
              </Box>

              {/* Manual Payment Info */}
              {payment.isManual && payment.paymentMethod && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {payment.paymentMethod === 'cash' ? (
                    <CashIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  ) : (
                    <AccountBalanceIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                  )}
                  <Box>
                    {payment.cardHolderName && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {payment.cardHolderName}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {getPaymentMethodLabel(payment.paymentMethod)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Card Info - for online payments */}
              {!payment.isManual && payment.cardLastFour && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Box>
                    {payment.cardHolderName && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {payment.cardHolderName}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {payment.cardType || 'Card'} ending in {payment.cardLastFour}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Transaction ID */}
              {payment.transactionId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Transaction ID:
                  </Typography>
                  {payment.transactionId.startsWith('ALEXA-') ? (
                    <Typography
                      component="a"
                      href={`https://sanalpos2.ziraatbank.com.tr/ziraat2/#/orders/d/${payment.transactionId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {payment.transactionId}
                    </Typography>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'monospace',
                        color: 'text.secondary',
                      }}
                    >
                      {payment.transactionId}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Added By (for manual payments) */}
              {payment.isManual && payment.addedBy && (
                <Typography variant="caption" color="text.secondary">
                  Added by: {payment.addedBy}
                </Typography>
              )}

              {payment.isManual && payment.editedBy && (
                <Typography variant="caption" color="text.secondary">
                  Last edited by: {payment.editedBy}
                </Typography>
              )}
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Summary */}
      <Divider sx={{ my: 2 }} />
      <Box
        sx={{
          p: 2,
          backgroundColor: remainingAmount > 0 ? 'warning.lighter' : 'success.lighter',
          borderRadius: 1,
        }}
      >
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Invoice Amount:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(invoiceAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Total Paid:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
              {formatCurrency(totalPaid)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              Remaining Balance:
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: remainingAmount > 0 ? 'warning.main' : 'success.main',
              }}
            >
              {formatCurrency(remainingAmount)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default PaymentHistorySubRow;
